import { useEffect, useMemo, useState } from "react";
import { useQuery } from "react-query";
import z from "zod";

import { useFoodpilotContext } from "src/context/FoodpilotContext";

import { zodParseWithLog } from "src/api/apiErrorLogs";
import { loggedInApiCall } from "src/api/axiosClient/userApi";
import { OlapScoresResultSchema } from "src/api/client-api/olapScores/olapScoresSchema";
import type { ProductFormType } from "src/pages/Product/ProductForm/ProductForm.types";
import {
    getIngredientLineScoreKey,
    getIngredientTransportLineScoreKey,
    getPackagingTransportLineScoreKey,
    getProductStageTransportLineScoreKey,
    getTransformationLineScoreKey,
    PRODUCT_TRANSPORT_LINE_SCORE_KEY,
} from "src/pages/Product/v2/ProductFormV2/sectionScores";
import { getProductIngredientTransportKey } from "src/pages/Product/v2/ProductFormV2/Steps/productIngredientTransportKey";

export const productLivePreviewKey = "productLivePreview" as const;

/** PHP/Symfony encodes empty associative arrays as `[]`; Zod records expect `{}`. */
const coerceEmptyRecord = (value: unknown): unknown => (Array.isArray(value) && value.length === 0 ? {} : value);

const ScoresMapSchema = z.preprocess(
    coerceEmptyRecord,
    z.record(z.string(), z.record(z.string(), z.number().nullable())),
);

export type ProductReferenceScoresMap = Record<string, number | null>;

const ProductReferenceScoresSchema = z.preprocess(
    coerceEmptyRecord,
    z.record(z.string(), z.number().nullable()).optional(),
);

const IngredientEcsBreakdownSchema = z.object({
    grossImpactPts: z.number(),
    ecosystemServicesPts: z.number(),
    totalImpactPts: z.number(),
    ecosystemServicesDetail: z.object({
        hedges: z.number(),
        plot_size: z.number(),
        crop_diversity: z.number(),
        permanent_pasture: z.number(),
    }),
});

export type IngredientEcsBreakdown = z.infer<typeof IngredientEcsBreakdownSchema>;

const ProductLivePreviewSchema = z.object({
    resolvedTransports: z.array(
        z.object({
            lineKey: z.string(),
            stage: z.string(),
            source: z.string(),
            label: z.string(),
            transportReferenceId: z.number().nullable(),
            distanceKm: z.number().nullable(),
            modeSummary: z
                .object({
                    legs: z
                        .array(
                            z.object({
                                name: z.string().nullable(),
                                transportModeId: z.number().nullable(),
                                distanceKm: z.number().nullable(),
                                isRefrigerated: z.boolean(),
                                originCountryCode: z.string().nullable(),
                                destinationCountryCode: z.string().nullable(),
                            }),
                        )
                        .default([]),
                })
                .optional(),
        }),
    ),
    lineScores: z.object({
        ingredients: ScoresMapSchema,
        transformations: ScoresMapSchema,
        packagings: ScoresMapSchema,
        transport: ScoresMapSchema,
        distribution: ScoresMapSchema,
        consumption: ScoresMapSchema,
    }),
    sectionScores: z.preprocess(
        coerceEmptyRecord,
        z.record(z.string(), z.preprocess(coerceEmptyRecord, z.record(z.string(), z.number().nullable()))),
    ),
    ingredientEcsBreakdown: z.preprocess(coerceEmptyRecord, z.record(z.string(), IngredientEcsBreakdownSchema)),
    olapScores: OlapScoresResultSchema,
    productReferenceScores: ProductReferenceScoresSchema,
    ecobalyseMasses: z.object({ totalEdibleMassG: z.number() }).optional(),
});

const normalizeNestedScoreMaps = (value: unknown): unknown => {
    const coerced = coerceEmptyRecord(value);
    if (coerced === null || typeof coerced !== "object" || Array.isArray(coerced)) {
        return coerced;
    }

    return Object.fromEntries(Object.entries(coerced).map(([key, nested]) => [key, coerceEmptyRecord(nested)]));
};

const normalizeLivePreviewPayload = (payload: unknown): unknown => {
    if (payload === null || typeof payload !== "object" || Array.isArray(payload)) {
        return payload;
    }

    const data = payload as Record<string, unknown>;
    const olapScores = coerceEmptyRecord(data.olapScores);

    const normalizedOlapScores =
        olapScores !== null && typeof olapScores === "object" && !Array.isArray(olapScores) ?
            Object.fromEntries(
                Object.entries(olapScores).map(([scoreId, score]) => {
                    if (score === null || typeof score !== "object" || Array.isArray(score)) {
                        return [scoreId, score];
                    }

                    return [
                        scoreId,
                        {
                            ...score,
                            subscores: coerceEmptyRecord((score as { subscores?: unknown }).subscores),
                        },
                    ];
                }),
            )
        :   olapScores;

    return {
        ...data,
        lineScores: normalizeNestedScoreMaps(data.lineScores),
        sectionScores: normalizeNestedScoreMaps(data.sectionScores),
        ingredientEcsBreakdown: coerceEmptyRecord(data.ingredientEcsBreakdown),
        productReferenceScores: coerceEmptyRecord(data.productReferenceScores),
        olapScores: normalizedOlapScores,
    };
};

export type ProductLivePreview = z.infer<typeof ProductLivePreviewSchema>;

type Options = {
    enabled?: boolean;
    debounceMs?: number;
    keepPreviousData?: boolean;
};

const useDebouncedValue = <T>(value: T, delay: number): T => {
    const [debouncedValue, setDebouncedValue] = useState(value);

    useEffect(() => {
        const timeout = window.setTimeout(() => setDebouncedValue(value), delay);

        return () => window.clearTimeout(timeout);
    }, [delay, value]);

    return debouncedValue;
};

export const buildProductLivePreviewInput = (product: ProductFormType, campaignId?: number | null) => {
    const productIngredients = product.productIngredients.map((ingredient) => ({
        ...ingredient,
        lineKey: getIngredientLineScoreKey(ingredient),
    }));

    return {
        id: product.id ?? null,
        campaignId: campaignId ?? null,
        ssqId: product.ssq_id ?? null,
        netWeight: product.netWeight,
        productReferenceId: product.productReference?.id ?? null,
        productIngredients,
        productTransformations: product.productTransformations.map((transformation) => ({
            ...transformation,
            lineKey: getTransformationLineScoreKey(transformation),
        })),
        productPackagings: product.productPackagings.map((packaging) => ({
            ...packaging,
            lineKey:
                packaging.packagingReference ?
                    `reference-${packaging.packagingReference.kind}-${packaging.packagingReference.id}`
                : packaging.packaging?.id !== undefined ? packaging.packaging.id.toString()
                : null,
        })),
        productTransports: (product.productTransports ?? [])
            .filter((transport) => {
                const isProductLevel =
                    transport.productIngredient == null &&
                    transport.productPackaging == null &&
                    transport.draftProductIngredientKey == null;

                if (!isProductLevel) {
                    return true;
                }

                return transport.transportCircuit.id > 0;
            })
            .map((transport) => ({
                ...transport,
                lineKey:
                    (typeof transport.lineKey === "string" && transport.lineKey !== "" ? transport.lineKey : null) ??
                    transport.draftProductIngredientKey ??
                    (() => {
                        const ingredient = product.productIngredients.find(
                            (item) => item.id === transport.productIngredient?.id,
                        );
                        return ingredient ? getProductIngredientTransportKey(ingredient) : null;
                    })() ??
                    getIngredientTransportLineScoreKey(transport.productIngredient?.id) ??
                    getPackagingTransportLineScoreKey(transport.productPackaging?.id) ??
                    (transport.productIngredient == null && transport.productPackaging == null ?
                        getProductStageTransportLineScoreKey(transport.stage)
                    :   PRODUCT_TRANSPORT_LINE_SCORE_KEY),
                transportCircuitId: transport.transportCircuit.id,
            })),
        distributionReferenceId: product.distribution?.distributionReferenceId ?? product.distributionReferenceId,
        distribution: product.distribution ?? null,
        productConsumptions: product.productConsumptions.map((consumption) => ({
            ...consumption,
            lineKey: `reference-${consumption.consumptionReferenceId}`,
        })),
    };
};

export const hasProductLivePreviewTarget = (input: ReturnType<typeof buildProductLivePreviewInput>): boolean =>
    input.productReferenceId != null ||
    input.productIngredients.length > 0 ||
    input.productTransformations.length > 0 ||
    input.productPackagings.length > 0 ||
    input.productTransports.length > 0 ||
    input.productConsumptions.length > 0 ||
    input.distributionReferenceId != null ||
    input.distribution != null;

const fetchProductLivePreview = async (input: ReturnType<typeof buildProductLivePreviewInput>) => {
    const request = await loggedInApiCall.post("/api/products/calculate-preview", input);

    return zodParseWithLog(normalizeLivePreviewPayload(request.data), ProductLivePreviewSchema, productLivePreviewKey);
};

export const useProductLivePreview = (product: ProductFormType, options: Options = {}) => {
    const { enabled = true, debounceMs = 350, keepPreviousData = false } = options;
    const { currentCampaign } = useFoodpilotContext();
    const input = useMemo(
        () => buildProductLivePreviewInput(product, currentCampaign?.id),
        [currentCampaign?.id, product],
    );
    const debouncedInput = useDebouncedValue(input, debounceMs);
    const inputFingerprint = JSON.stringify(input);
    const debouncedInputFingerprint = JSON.stringify(debouncedInput);

    const query = useQuery({
        queryKey: [productLivePreviewKey, debouncedInput],
        enabled,
        keepPreviousData,
        staleTime: 0,
        cacheTime: 0,
        retry: 1,
        queryFn: async () => ({
            inputFingerprint: debouncedInputFingerprint,
            preview: await fetchProductLivePreview(debouncedInput),
        }),
    });
    const isInputPending =
        enabled &&
        (inputFingerprint !== debouncedInputFingerprint ||
            (query.isFetching && (query.data === undefined || query.data.inputFingerprint !== inputFingerprint)) ||
            (query.data !== undefined && query.data.inputFingerprint !== inputFingerprint));

    return {
        ...query,
        data: enabled ? query.data?.preview : undefined,
        isInputPending,
    };
};
